import { generateHTML, generateJSON, type JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import { BlockIndent } from './blockIndent';
// Explicit versioning keeps legacy plain text (including literal HTML) literal.
import { RICH_TEXT_PREFIX } from '../../../../shared/richTextFlatten.js';

export const richTextExtensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    code: false,
    codeBlock: false,
    link: { openOnClick: false, protocols: ['https', 'http', 'mailto', 'tel'] },
  }),
  TextStyleKit,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  BlockIndent,
];

const escapeText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
export const plainTextHtml = (value: string) => `<p>${escapeText(value).replace(/\r\n?|\n/g, '<br>')}</p>`;
export const richTextInput = (value: string) => value.startsWith(RICH_TEXT_PREFIX) ? value.slice(RICH_TEXT_PREFIX.length) : plainTextHtml(value);

// The bullet and number markers the proposal print reads from plain text (proposalPrintPages BULLET).
const LIST_LINE = /^(\s*)(?:[•◦▪‣·*-]|(\d+)[.)])\s+(.*)$/;
/**
 * Plain text typed as a list ("- " or "1. " lines, indented to nest) as real lists, other lines as paragraphs, so it
 * opens in the editor as bullets that indent. Only the editor's view: the stored value stays plain until edited.
 */
export function plainTextListsHtml(value: string): string {
  let html = '';
  const open: Array<{ indent: number; tag: 'ul' | 'ol' }> = [];
  const closeList = () => { html += `</li></${open.pop()!.tag}>`; };
  for (const raw of value.split(/\r\n?|\n/)) {
    const match = raw.match(LIST_LINE);
    if (!match) {
      while (open.length) closeList();
      if (raw.trim()) html += `<p>${escapeText(raw.trim())}</p>`;
      continue;
    }
    const indent = match[1].replace(/\t/g, '    ').length;
    const tag = match[2] ? 'ol' : 'ul';
    while (open.length && open[open.length - 1].indent > indent) closeList();
    if (open.length && open[open.length - 1].indent === indent && open[open.length - 1].tag !== tag) closeList();
    const top = open[open.length - 1];
    if (top && top.indent === indent) html += '</li><li>';
    else { html += `<${tag}><li>`; open.push({ indent, tag }); }
    html += `<p>${escapeText(match[3].trim())}</p>`;
  }
  while (open.length) closeList();
  return html || '<p></p>';
}
export const richTextListsInput = (value: string) => value.startsWith(RICH_TEXT_PREFIX) ? value.slice(RICH_TEXT_PREFIX.length) : plainTextListsHtml(value);
export const serializeRichText = (html: string) => `${RICH_TEXT_PREFIX}${html}`;

/** Parse through the editor schema before rendering; never render stored HTML directly. */
export function richTextHtml(value: string): string {
  return generateHTML(generateJSON(richTextInput(value), richTextExtensions), richTextExtensions);
}

/** Plain-text consumers (previews and event note rows) must never display markup. */
export function richTextPlainText(value: string): string {
  if (!value.startsWith(RICH_TEXT_PREFIX)) return value;
  const text = (node: JSONContent): string => {
    if (node.type === 'text') return node.text || '';
    if (node.type === 'hardBreak') return '\n';
    const content = (node.content || []).map(text).join('');
    return ['paragraph', 'heading', 'listItem', 'blockquote'].includes(node.type || '') ? `${content}\n` : content;
  };
  return text(generateJSON(richTextInput(value), richTextExtensions)).trim();
}

export function appendRichText(value: string, text: string): string {
  if (!text.trim()) return value;
  return value.startsWith(RICH_TEXT_PREFIX)
    ? serializeRichText(`${richTextHtml(value)}${plainTextHtml(text)}`)
    : [value, text].filter(Boolean).join('\n\n');
}
